import { I18nProvider } from 'react-aria-components/I18nProvider';
import { Router } from 'routes';

import { IApiClient } from '../app/data/client';
import { HttpError } from '../app/data/HttpError';
import { IPageState, nameOf } from '../app/shared/common';
import { INavigationService } from '../app/shared/NavigationService';
import { ModuleTranslation } from './localization/ModuleTranslation';
import ModuleTranslationResolver from './localization/ModuleTranslationResolver';
import { PricingContext } from './model';
import { ModuleRoutes, PricelistRouteParams, ProductRouteParams } from './ModuleRoutes';
import PricelistDetails from './routes/PricelistDetails/PricelistDetails';
import PricelistDetailsState from './routes/PricelistDetails/PricelistDetailsState';
import Pricelists from './routes/Pricelists/Pricelists';
import PricelistsState from './routes/Pricelists/PricelistsState';
import { ProductDetails } from './routes/ProductDetails/ProductDetails';
import ProductDetailsState from './routes/ProductDetails/ProductDetailsState';
import { ToastRegion } from './ui/Toast/Toast';

interface Page {
  state: IPageState;
  component: JSX.Element;
}

type RouteParams = Record<string, string>;

interface Route {
  route: string;
  getPage: (moduleState: ModuleState, params: RouteParams) => Page;
}

class ModuleState {
  public readonly translation: ModuleTranslation;
  public readonly routes: ModuleRoutes;

  public constructor(
    public readonly client: IApiClient,
    public readonly navigation: INavigationService,
    public readonly pricingContext: PricingContext,
    public readonly currentPath: string,
    basePath: string,
  ) {
    this.translation = ModuleTranslationResolver.resolve(pricingContext.languageCode);
    this.routes = new ModuleRoutes(basePath);
  }
}

class ModuleRouteDefinitions {
  public static instance = new ModuleRouteDefinitions();

  public listingRoute: Route = {
    route: ModuleRoutes.paths.listing,
    getPage(moduleState: ModuleState) {
      const state = new PricelistsState(
        moduleState.client,
        moduleState.pricingContext,
        moduleState.translation.pricelists,
        moduleState.routes,
        moduleState.navigation,
      );
      const component = <Pricelists state={state} />;

      return { state, component };
    },
  };
  public pricelistDetailsRoute: Route = {
    route: ModuleRoutes.paths.pricelistDetails,
    getPage(moduleState: ModuleState, params: RouteParams) {
      const state = new PricelistDetailsState(
        moduleState.client,
        moduleState.pricingContext,
        moduleState.translation.pricelistDetails,
        moduleState.routes,
        moduleState.navigation,
        params[nameOf<PricelistRouteParams>('id')],
      );
      const component = <PricelistDetails state={state} />;

      return { state, component };
    },
  };
  public productDetailsRoute: Route = {
    route: ModuleRoutes.paths.productDetails,
    getPage(moduleState: ModuleState, params: RouteParams) {
      const state = new ProductDetailsState(
        moduleState.client,
        moduleState.pricingContext,
        moduleState.translation.productDetails,
        moduleState.routes,
        moduleState.navigation,
        params[nameOf<ProductRouteParams>('id')],
      );
      const component = <ProductDetails state={state} />;

      return { state, component };
    },
  };
}

class ModuleRouter {
  private readonly router = Router<Route>();

  public constructor(routes: Route[]) {
    routes.forEach((route) => this.router.addRoute(route.route, route));
  }

  public resolve(
    client: IApiClient,
    navigation: INavigationService,
    pricingContext: PricingContext,
    path: string,
    basePath: string,
  ): Page {
    const state = new ModuleState(client, navigation, pricingContext, path, basePath);
    // The host mounts this module, so paths arrive relative to the mount point and the mount point
    // itself arrives as an empty path; the router only matches `/`, so normalise it before matching.
    const match = this.router.match(state.currentPath || ModuleRoutes.paths.listing);

    if (!match) {
      throw new HttpError(404, `No pricelists route matches '${path}'`);
    }

    const page = match.fn.getPage(state, match.params);

    // The region belongs to the module rather than to a route: a confirmation raised by one view has
    // to survive that view — a modal closing, or a navigation inside the module. `I18nProvider` puts
    // react-aria's own strings — the region's landmark name among them — in the store's language
    // instead of the visitor's browser language, which is what it falls back to.
    return {
      state: page.state,
      component: (
        <I18nProvider locale={state.pricingContext.languageCode}>
          {page.component}
          <ToastRegion />
        </I18nProvider>
      ),
    };
  }
}

const RouteDefinitions = ModuleRouteDefinitions.instance;
export const PricelistsRouter = new ModuleRouter([
  RouteDefinitions.listingRoute,
  RouteDefinitions.pricelistDetailsRoute,
  RouteDefinitions.productDetailsRoute,
]);
